import { useEffect, useState } from 'react';
import { useAuth0Safe as useAuth0 } from '@/hooks/useAuth0Safe';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Bookmark, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function getToken(authed, getAccessTokenSilently) {
  try {
    const local = localStorage.getItem('emina_local_admin_token');
    if (local) return Promise.resolve(local);
  } catch {}
  if (authed) return getAccessTokenSilently().catch(() => null);
  return Promise.resolve(null);
}

export default function SavedPage() {
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [needAuth, setNeedAuth] = useState(false);

  async function load() {
    setLoading(true);
    const t = await getToken(isAuthenticated, getAccessTokenSilently);
    if (!t) { setNeedAuth(true); setLoading(false); return; }
    try {
      const r = await fetch(`${API}/public/bookmarks`, { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setItems(d.bookmarks || []);
    } catch { toast.error('Greška pri učitavanju'); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [isAuthenticated]);

  async function remove(id) {
    const t = await getToken(isAuthenticated, getAccessTokenSilently);
    if (!t) return;
    await fetch(`${API}/public/bookmarks/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${t}` } });
    setItems(items.filter(b => b.article_id !== id));
    toast.success('Uklonjeno');
  }

  if (loading) return <div className="text-center py-20 text-gray-400">Učitavanje…</div>;

  if (needAuth) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="saved-need-auth">
        <Bookmark size={48} className="text-amber-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold mb-3">Spremljeni članci</h1>
        <p className="text-gray-500 mb-6">Prijavite se da biste spremali i pregledavali svoje članke.</p>
        <button onClick={() => loginWithRedirect()} className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-medium text-sm hover:bg-red-700" data-testid="saved-login-btn">
          Prijavi se
        </button>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Spremljeni članci — e-mina.eu</title></Helmet>
      <div className="max-w-5xl mx-auto px-4 py-10" data-testid="saved-page">
        <h1 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-2">
          <Bookmark className="text-amber-500" /> Spremljeni članci
        </h1>
        {items.length === 0 ? (
          <div className="text-gray-500 bg-gray-50 dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-lg px-6 py-10 text-center">
            Još nemate spremljenih članaka. Kliknite ikonu oznake na članku da biste ga dodali.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map(b => {
              const a = b.article;
              const cover = a.cover_image_url?.startsWith('http') ? a.cover_image_url : `${BACKEND}${a.cover_image_url || ''}`;
              return (
                <div key={b.id} className="bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden hover:shadow-md transition-shadow" data-testid={`saved-item-${a.id}`}>
                  <Link to={`/clanak/${a.slug}`} className="block">
                    {a.cover_image_url && <div className="aspect-[16/9] bg-gray-100 dark:bg-gray-900"><img src={cover} alt="" className="w-full h-full object-cover" /></div>}
                    <div className="p-4">
                      <h3 className="font-bold text-gray-900 dark:text-gray-100 line-clamp-2 mb-1">{a.title_hr}</h3>
                      <p className="text-xs text-gray-500 line-clamp-2">{a.excerpt_hr}</p>
                    </div>
                  </Link>
                  <div className="px-4 pb-3 flex justify-end">
                    <button onClick={() => remove(a.id)} className="text-xs text-gray-500 hover:text-red-600 inline-flex items-center gap-1" data-testid={`saved-remove-${a.id}`}>
                      <Trash2 size={12} /> Ukloni
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
